import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { assumeRole, clearAssumedRole, getMe } from './api.js';
import { signOut, startSession } from './auth/session.js';
import SignInWall from './auth/SignInWall.jsx';
import { enabledEntries, entryAllowed, entryOf, homeFor } from './features.js';
import { MeContext, useMe } from './MeContext.js';
import NotificationBell from './notifications/NotificationBell.jsx';
import AureminSymbol from './ui/AureminSymbol.jsx';
import Icon from './ui/Icon.jsx';
import Navigation from './ui/Navigation.jsx';
import { notify, ToastHost } from './ui/notify.jsx';

// The authentication shell and layout. Everything about deals lives in routes underneath it, so
// this file has one job: establish who the caller is, and show the wall until it knows. The
// entries it then offers are the features the environment has on (features.js, ADR-0005).

export default function App() {
  const [state, setState] = useState({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;

    startSession(async (session) => {
      if (cancelled) return;

      if (session.phase !== 'authenticated') {
        setState(session);
        return;
      }

      // A sign-in arrives here from the signed-out wall as well as on first load; the bar shows
      // for the /api/me round trip either way.
      setState({ phase: 'loading' });

      try {
        // /api/me registers a first-time signer as an inert record and reports whether an
        // administrator has activated them and what roles they hold (architecture.md section 5.3).
        // That is the real authorisation path, and it runs locally too. Every read requires a role
        // (ADR-0007), so an active account with none can reach nothing - it waits like a pending one
        // rather than landing on screens that would only 403.
        const me = await getMe();
        const admitted = me.active && me.roles.length > 0;
        setState({ phase: admitted ? 'ready' : 'pending', me });
      } catch (err) {
        setState({ phase: 'error', message: err.message });
      }
    }).catch((err) => {
      if (!cancelled) setState({ phase: 'error', message: err.message });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const updateMe = useCallback((me) => {
    setState((prev) => ({ ...prev, me }));
  }, []);

  if (state.phase !== 'ready') {
    return <SignInWall phase={state.phase} me={state.me} message={state.message} />;
  }

  return (
    <Shell me={state.me} onMeChanged={updateMe}>
      <MeContext.Provider value={state.me}>
        <FeatureGate>
          <Outlet />
        </FeatureGate>
      </MeContext.Provider>
    </Shell>
  );
}

// A screen whose feature is off in this environment is not there: a bookmarked or shared link
// goes home instead. The API answers 404 for the same reason; this only spares the round trip.
// So is a screen whose entry names roles the caller does not hold - the API would answer 403.
function FeatureGate({ children }) {
  const me = useMe();
  const { pathname } = useLocation();
  const entry = entryOf(pathname);
  if (entry && (!me.features.includes(entry.feature) || !entryAllowed(entry, me.roles))) {
    return <Navigate to={homeFor(me.features, me.roles) ?? '/'} replace />;
  }
  return children;
}

// The index route: the first enabled entry, or the one line there is to say when nothing is on.
export function Home() {
  const me = useMe();
  const home = homeFor(me.features, me.roles);
  if (home) return <Navigate to={home} replace />;
  return <p className="empty">Nothing is enabled in this environment.</p>;
}

// A gate that only renders its children for admin users. Non-admins see a brief message and are
// redirected home. Used on the settings routes; the cog that leads to them is not rendered either,
// so this is defence in depth for deep links.
export function AdminGate({ children }) {
  const me = useMe();
  const navigate = useNavigate();
  if (!me.roles.includes('ADMIN')) {
    return (
      <div className="empty">
        <p>You do not have access to this page.</p>
        <button type="button" className="secondary" onClick={() => navigate('/')}>
          Go home
        </button>
      </div>
    );
  }
  return children;
}

// The roles an admin may assume (ADR-0008). ADMIN is not in the list — it is always present. An
// assumed role adds to the roster's roles; clearing it leaves exactly the roster's.
const ASSUMABLE_ROLES = [
  'USER', 'OPERATIONS', 'FINANCE', 'COMPLIANCE', 'RISK', 'LEGAL', 'COMMERCIAL',
];

// The frame around every screen: the bar with the mark, the navigation and who they are. Only
// rendered once someone is signed in and activated; before that there is the wall, and no bar.
function Shell({ me, onMeChanged, children }) {
  const entries = enabledEntries(me.features, me.roles);
  const isAdmin = me.roles.includes('ADMIN');
  const assumedRole = (isAdmin && me.assumed_role) || '';
  const envLabel = me.environment !== 'prod' ? me.environment?.toUpperCase() : null;

  const onAssumeRole = async (event) => {
    const role = event.target.value;
    try {
      const updated = role ? await assumeRole(role) : await clearAssumedRole();
      onMeChanged(updated);
      notify(role ? `Acting as ${role}` : 'Back to your roster roles');
    } catch (err) {
      notify(err.message);
    }
  };

  return (
    <>
      {envLabel && <div className="env-strip" aria-hidden="true" />}
      <header className="bar">
        <div className="bar-inner">
          {/* The way home is named for the application; the symbol inside it is named for the
              company (TX-US-0018), and the word repeats the link's name, so it is decoration. */}
          <Link to={homeFor(me.features, me.roles) ?? '/'} className="brand" aria-label="T-REX">
            <AureminSymbol size={28} />
            <span aria-hidden="true">T-REX</span>
          </Link>
          {envLabel && <span className="env-badge">{envLabel}</span>}

          {entries.length > 0 && <Navigation entries={entries} />}

          <div className="who">
            <span className="who-name">
              {me.display_name || me.email}
              {me.roles.length > 0 && <> · {me.roles.join(', ')}</>}
            </span>
            {isAdmin && (
              <select className="assume-role" value={assumedRole} onChange={onAssumeRole}
                      aria-label="Assume role" title="Assume role">
                <option value="">Roster roles</option>
                {ASSUMABLE_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            )}
            {isAdmin && (
              <Link to="/settings" className="icon-button quiet" aria-label="Settings"
                    title="Settings">
                <Icon name="settings" />
              </Link>
            )}
            <NotificationBell />
            <button type="button" className="icon-button quiet" onClick={signOut}
                    aria-label="Sign out" title="Sign out">
              <Icon name="signOut" />
            </button>
          </div>
        </div>
      </header>

      <main>
        {children}
        {/* Here rather than around a route: a confirmation is raised by the screen that is being
            left, and has to outlive it. */}
        <ToastHost />
      </main>
    </>
  );
}
